/**
 * Jovii — SHIMMER ONLY WHERE IT CAN BE SEEN.
 *
 * The loading sweep (`Skeleton.svelte`, `Art.svelte`) is a `background-position`
 * keyframe — main-thread paint, every frame, for as long as the element is
 * pending. That is right for the six placeholders under the reader's thumb and
 * disastrous in aggregate: a cold phone Home holds ~250 skeletons in unspotted
 * lazy rails plus every below-the-fold poster whose `loading="lazy"` image has
 * not been fetched, and each one runs the sweep forever. Measured at 390×844
 * (6× CPU throttle, headless): 273 concurrent sweeps, one style-recalc + paint
 * pass every ~85ms with the page IDLE — a permanent tax that competes with
 * every scroll frame, and the reason "even standing still" the phone felt busy
 * (docs/PERF-SCROLL.md).
 *
 * So the sweep is gated: ONE shared IntersectionObserver marks a shimmering
 * element `lit` while it is within a viewport-and-a-bit of the glass, and only
 * `.lit` animates. The gradient's resting frame is fully transparent, so an
 * unlit placeholder looks exactly like a lit one between sweeps — nothing
 * visible changes, only the offscreen work stops. Class toggles happen from the
 * observer callback, off the render path, and cost one invalidation for one
 * element each.
 *
 * Where `IntersectionObserver` is missing the gate fails OPEN (everything lit):
 * the sweep is a loading signal, and losing the optimisation is better than
 * losing the signal.
 */

let io: IntersectionObserver | null | undefined;

function observer(): IntersectionObserver | null {
  if (io !== undefined) return io;
  if (typeof IntersectionObserver !== 'function') {
    io = null;
    return io;
  }
  io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle('lit', entry.isIntersecting);
      }
    },
    // Half a viewport of margin: a placeholder starts sweeping just before it
    // scrolls on, so a reader never catches one starting up.
    { rootMargin: '50%' }
  );
  return io;
}

/** `<div class="skeleton" use:sweepGate>` — that is the whole contract. */
export function sweepGate(node: HTMLElement): { destroy(): void } {
  const o = observer();
  if (o === null) {
    node.classList.add('lit');
    return { destroy: () => {} };
  }
  o.observe(node);
  return {
    destroy: () => {
      o.unobserve(node);
    }
  };
}
